import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useMutation } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ButtonBase from '@mui/material/ButtonBase'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { debounce } from 'lodash';

import { DELETE_MEAL, UPDATE_MEAL_SERVING_SIZE } from '../../../app/graphql/mutations';
import { UserMealPlanDataSelector } from '../../../app/mealPlan/mealPlan.slice';
import { useAppSelector } from '../../../app/store/hooks';
import useLogRecipeEdit from '../../../hooks/useLogRecipeEdit';
import { CookingTimeIcon, DeleteIcon, MealCooked, MealMissed, PrepTimeIcon, ServingSize } from '../../../iconRef/mealPLanIcons';
import Loader from '../../../pages/mealPlan/Loader';
import { EventLogsEnum } from '../../../utils/events';
import { resizeImageIfCloudinary } from '../../../utils/images/cloudinaryUtils';
import { CategoryIcon } from '../../recipeCardSubComponents';
import { FINISHED_MEAL_STATUSES } from '../constants';
import useUpdateGroceryList from '../../../hooks/groceryList/useUpdateGroceryList';
import { Meal, Status } from '../mealPlan.types';
import { decreaseServingSize, formatTime, increaseServingSize } from '../utils';
import styles from './MealCard.module.css'

// type MealStatus = 'cooked' | 'completed' | 'ended' | 'pending' | 'missed';

const MealCard = ({ meal, refetchMeals, isPastMeal, updateMealServingSize: updateMealServingSizeRedux }: { meal: Meal, refetchMeals: () => Promise<void>, isPastMeal: boolean, updateMealServingSize: (mealId: string, servingSize: number) => void }) => {
    const [updateMealServingSize, { loading: updateMealServingLoader }] = useMutation(UPDATE_MEAL_SERVING_SIZE);
    const history = useHistory();
    const { logRecipeEdit } = useLogRecipeEdit();

    const recipeData = meal.Recipes_E3;
    const mealStatus: Status = meal?.status ? meal.status.toLowerCase() as Status : 'pending';
    const mealIsFinished = FINISHED_MEAL_STATUSES.includes(mealStatus);
    const userMealPlanData = useAppSelector(UserMealPlanDataSelector);
    const meal_plan_id = userMealPlanData?.meal_plan_id;
    const cookingTime = formatTime(recipeData.Recipe_Variations[0]?.cooking_time)
    const prepTime = formatTime(recipeData.Recipe_Variations[0]?.prep_time)
    const { updatingGroceryListFromServingSize, updateGroceryListFromServingSize } = useUpdateGroceryList();
    const [defaultServingSize, setDefaultServingSize] = useState(meal?.serving_size ?? 2);
    const maxServing = recipeData.Recipe_Variations[0]?.max_serving;
    const minServing = recipeData.Recipe_Variations[0]?.min_serving;
    const { updatingGroceryListFromRemovingMeal, updateGroceryListFromRemovingMeal } = useUpdateGroceryList();

    const debouncedUpdateServingSize = useCallback(debounce(async (mealId: string, currentServingSize: number, newServingSize: number) => {
        try {
            if (currentServingSize !== newServingSize) {
                updateMealServingSizeRedux(mealId, newServingSize)
                const data = await updateMealServingSize({
                    variables: {
                        id: mealId,
                        serving_size: newServingSize
                    }
                })
                const updatedMealData = data?.data?.update_Meal_by_pk;

                await updateGroceryListFromServingSize([updatedMealData]);

            }
            logRecipeEdit("EDIT_MEAL", {
                meal_plan_id,
                eventData: {
                    recipe_id: meal?.recipe_id,
                    meal_id: mealId,
                    scheduled_date_time: meal?.scheduled_date_time,
                    new_data: {
                        serving_size: newServingSize
                    },
                    old_data: {
                        serving_size: currentServingSize
                    }
                },
            });

        }
        catch (error) {
            console.error("a: Failed to update serving size:", error);
            setDefaultServingSize(currentServingSize);
        }
    }, 500), [updateMealServingSize, logRecipeEdit, updateMealServingSizeRedux, meal?.recipe_id, meal?.scheduled_date_time]);

    const addServing = useCallback(() => {
        const newServingSize = increaseServingSize(defaultServingSize, maxServing);
        if (defaultServingSize !== newServingSize) debouncedUpdateServingSize(meal.id, defaultServingSize, newServingSize);
        setDefaultServingSize(newServingSize);
    }, [defaultServingSize, maxServing, meal.id, debouncedUpdateServingSize]);

    const subtractServing = useCallback(() => {
        const newServingSize = decreaseServingSize(defaultServingSize, minServing);
        if (defaultServingSize !== newServingSize) debouncedUpdateServingSize(meal.id, defaultServingSize, newServingSize);
        setDefaultServingSize(newServingSize);

    }, [defaultServingSize, minServing, meal.id, debouncedUpdateServingSize]);

    const handleRecipeOpen = () => {
        if (meal?.recipe_id) {
            history.push(`/recipe/${meal?.recipe_id}`);
            logRecipeEdit(EventLogsEnum.APP_RECIPE_OPENED, {
                eventData: { recipeId: meal.recipe_id, source: "meal-plan" },
            });
        }
    };

    const [deleteMeal, { loading: deletingMeal }] = useMutation(DELETE_MEAL);
    const [translateX, setTranslateX] = useState(0);
    const [startX, setStartX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    const handleMealDelete = async (mealId: string) => {
        await updateGroceryListFromRemovingMeal({ mealId });
        await deleteMeal({
            variables: {
                id: mealId
            }
        })

        logRecipeEdit("DELETE_MEAL", {
            meal_plan_id,
            eventData: {
                meal_data: meal
            },
        });

        await refetchMeals();

    }

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        setStartX(touch.clientX);
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (!isDragging) return;

        const touch = e.touches[0];
        const currentX = touch.clientX;
        const deltaX = currentX - startX;

        let newTranslateX = translateX + deltaX;
        if (newTranslateX > 0) {
            newTranslateX = 0;
        } else if (newTranslateX < -100) {
            newTranslateX = -100;
        }

        setTranslateX(newTranslateX);
        setStartX(currentX);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        if (translateX > -100) {
            setTranslateX(0);
        }
    };

    return <Grid className={styles.meal_card_wrapper}>
        <Grid onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
            onTouchStart={handleTouchStart}
            style={{
                zIndex: 2,
                transform: `translateX(${translateX}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease',
                width: "100%"
            }}>
            <Grid className={styles.recipe_card}>

                <Grid className={styles.recipe_image} onClick={handleRecipeOpen}>
                    <img alt={recipeData.name} src={resizeImageIfCloudinary(recipeData.Recipe_Variations[0]?.image_url, 90, 90, -1, -1, true)} />

                    {mealIsFinished ? (
                        <Grid className={styles.status_overlay_status}>
                            <MealCooked fontSize="large" />
                            <Typography variant="caption4LightOutfit">
                                Cooked
                            </Typography>
                        </Grid>
                    ) : isPastMeal ? (
                        <Grid className={styles.status_overlay_status}>
                            <MealMissed fontSize="large" />
                            <Typography variant="caption4LightOutfit">
                                Missed
                            </Typography>
                        </Grid>
                    ) : null}
                </Grid>

                <Grid className={styles.recipe_details} onClick={handleRecipeOpen}>
                    <Typography className={styles.recipe_title} variant='ingredientText1Outfit'>{recipeData.name}</Typography>
                    <Grid alignItems="center" className={styles.recipe_info}>

                        {cookingTime ?
                            <Grid className={styles.cooking_time_container}>
                                <CookingTimeIcon fontSize="small" />
                                <Typography variant='caption4LightOutfit'>{cookingTime}</Typography>
                            </Grid> : null
                        }
                        {prepTime ?
                            <Grid className={styles.cooking_time_container}>
                                <PrepTimeIcon fontSize="small" />
                                <Typography variant='caption4LightOutfit'>{prepTime}</Typography>
                            </Grid> : null}
                    </Grid>
                    <Grid className={styles.recipe_extra_info}>
                        <Typography className={styles.recipe_kcal} component="p" variant="caloriesText">{recipeData?.Recipe_Variations[0]?.metadata?.nutritionalValues?.calories ?? "0"} kCal</Typography>

                        {recipeData?.diet?.length > 0 ?
                            <Grid className={styles.recipe_diet}>
                                <CategoryIcon category={recipeData.diet[0]} recipeName={recipeData.name} />
                                <Typography className={styles.recipe_cuisine} noWrap variant='caloriesText'>{recipeData.cuisine.join(' • ')}</Typography>
                            </Grid>
                            : null}
                    </Grid>
                </Grid>

                {!isPastMeal && !mealIsFinished ? <Grid className={styles.recipe_actions}>
                    <ButtonBase className={styles.add_button} onClick={addServing}>
                        <AddIcon fontSize='small' />
                    </ButtonBase>
                    <Grid className={styles.recipe_serving_size}>
                        <ServingSize />
                        <Typography variant='caption4LightOutfit'>{defaultServingSize}</Typography>
                    </Grid>
                    <ButtonBase className={styles.remove_button} onClick={subtractServing}>
                        <RemoveIcon fontSize='small' />
                    </ButtonBase>
                </Grid> : null}
            </Grid>
        </Grid>
        <ButtonBase
            className={styles.delete_btn}
            onClick={() => handleMealDelete(meal?.id)}
        >
            <DeleteIcon />
            <Typography variant='caption4LightOutfit'>Delete</Typography>
        </ButtonBase>
        <Loader loading={deletingMeal || updateMealServingLoader || updatingGroceryListFromServingSize || updatingGroceryListFromRemovingMeal} />
    </Grid>

}

export default MealCard

// 
